import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import "./style.scss";
import axios from "axios";
import DialogMessage from "../../DialogMessage";
import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { darken } from "@mui/system";

const salutations = [
  { value: "Mr.", label: "Mr." },
  { value: "Ms.", label: "Ms." },
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
];

const RegisterGroupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ Check if free mode from URL
  const isFreeMode = searchParams.get("type") === "free";

  const extractConferenceIdFromUrl = () => {
    const path = location.pathname;
    const parts = path.split("/");
    return parts[parts.length - 1];
  };

  const conferenceId = extractConferenceIdFromUrl();

  const routeState = location.state || {};
  const selectedGroupPrices = routeState.selectedGroupPrices || [];

  // ✅ Excel file states - ONLY for FREE mode
  const [excelFile, setExcelFile] = useState(null);
  const [excelFileName, setExcelFileName] = useState("");

  const [organizationName, setOrganizationName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [password] = useState("Text@7456");
  const [email, setEmail] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [countryOfResidence, setCountryOfResidence] = useState("");
  const [title, setTitle] = useState("");

  const [doctorsPerPackage, setDoctorsPerPackage] = useState({});

  const [error, setError] = useState({
    organizationName: "",
    contactPerson: "",
    phone: "",
    password: "",
    email: "",
    companyAddress: "",
    doctorsRegistered: "",
    whatsappNumber: "",
    countryOfResidence: "",
    title: "",
    excelFile: "",
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(
    "Your organization has been successfully registered! You can now proceed to payment."
  );

  const [registrationId, setRegistrationId] = useState(null);

  const [formData, setFormData] = useState({
    accept_cancellation_policy: false,
  });

  const [registering, setRegistering] = useState(false);

  // Countries dropdown state
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const res = await axios.get("https://eventscons.com/eventbackendnew/api/countries", {
          headers: { Accept: "application/json" },
        });
        setCountries(res.data?.data || []);
      } catch (e) {
        console.error("Countries API error:", e);
        toast.error("Failed to load countries.");
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  const perPackageTotals = useMemo(() => {
    // ✅ Free mode: ZERO price calculation
    if (isFreeMode) {
      return selectedGroupPrices.map((pkg) => {
        const doctors = Number(doctorsPerPackage[pkg.id] || 0);
        return {
          pkgId: pkg.id,
          doctors,
          price: 0,
          total: 0,
        };
      });
    }

    // Normal paid mode calculation
    return selectedGroupPrices.map((pkg) => {
      const doctors = Number(doctorsPerPackage[pkg.id] || 0);
      const price = Number(pkg.price_per_person || 0);
      return {
        pkgId: pkg.id,
        doctors,
        price,
        total: doctors * price,
      };
    });
  }, [selectedGroupPrices, doctorsPerPackage, isFreeMode]);

  const grandTotal = useMemo(
    () => perPackageTotals.reduce((sum, row) => sum + row.total, 0),
    [perPackageTotals]
  );

  const totalDoctors = useMemo(
    () => perPackageTotals.reduce((sum, row) => sum + row.doctors, 0),
    [perPackageTotals]
  );

  const isFreeOnly = isFreeMode || grandTotal === 0;
  const shouldShowPayNow = !isFreeOnly && grandTotal > 0;

  const handleDoctorsChange = (pkgId, value) => {
    const numeric = value === "" ? "" : Number(value);
    if (numeric === "" || !Number.isNaN(numeric)) {
      setDoctorsPerPackage((prev) => ({
        ...prev,
        [pkgId]: value,
      }));
    }
  };

  // ✅ FIXED: Phone merge function - WhatsApp prefer karega
  const getFinalPhone = () => {
    return whatsappNumber || phone || "";
  };

  // ✅ Excel file handler
  const handleExcelFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (allowedTypes.includes(selectedFile.type) || selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setExcelFile(selectedFile);
        setExcelFileName(selectedFile.name);
        setError(prev => ({ ...prev, excelFile: "" }));
      } else {
        toast.error("Please select a valid Excel file (.xlsx or .xls)");
        setExcelFile(null);
        setExcelFileName("");
        setError(prev => ({ 
          ...prev, 
          excelFile: "Invalid file type. Please upload Excel file (.xlsx or .xls)." 
        }));
      }
    }
  };

  // ✅ FIXED: Complete payload with salutation + Excel support
  const handleSubmit = async () => {
    if (!formData.accept_cancellation_policy) {
      toast.error("Please accept the cancellation and refund policy.");
      return;
    }

    const packageIds = selectedGroupPrices.map((pkg) => pkg.id);
    const numberOfDoctorsPerCategory = { general: totalDoctors };

    const payload = {
      name: `${title} ${organizationName || contactPerson}`.trim(), // ✅ Salutation + name
      email: email.trim(),  // ✅ Email properly formatted
      phone: getFinalPhone(),  // ✅ WhatsApp OR Phone
      contact_person: contactPerson,
      conference_id: Number(conferenceId),
      password,
      package_ids: packageIds,
      number_of_doctors_per_category: numberOfDoctorsPerCategory,
      country_of_residence: countryOfResidence,
    };

    // ✅ FREE MODE: Excel MANDATORY check
    if (isFreeMode && (!excelFile || !excelFileName)) {
      toast.error("Excel file is mandatory for free registration.");
      return;
    }

    // ✅ DEBUG LOGS - Console mein dekho payload
    console.log("🔥 FINAL PAYLOAD:", JSON.stringify(payload, null, 2));
    console.log("📱 PHONE:", payload.phone);
    console.log("📦 PACKAGE_IDS:", packageIds);
    console.log("🌐 FREE MODE:", isFreeMode);
    console.log("📊 EXCEL:", excelFileName);

    try {
      setRegistering(true);

      // ✅ CORRECT API ENDPOINT based on free/paid
      const apiEndpoint = isFreeMode
        ? "https://eventscons.com/eventbackendnew/api/group-free-registrations"  // ✅ FREE endpoint
        : "https://eventscons.com/eventbackendnew/api/group-registrations";     // Paid endpoint

      let response;

      if (isFreeMode && excelFile) {
        // ✅ FREE MODE: FormData with proper array handling
        const formDataToSend = new FormData();
        
        formDataToSend.append('name', payload.name);
        formDataToSend.append('email', payload.email);
        formDataToSend.append('phone', payload.phone);
        formDataToSend.append('contact_person', payload.contact_person || '');
        formDataToSend.append('conference_id', payload.conference_id);
        formDataToSend.append('password', payload.password);
        
        // ✅ Multiple appends for array - Laravel ko array milega
        packageIds.forEach(id => {
          formDataToSend.append('package_ids[]', id);
        });
        
        formDataToSend.append('number_of_doctors_per_category[general]', totalDoctors);
        formDataToSend.append('country_of_residence', payload.country_of_residence || '');
        formDataToSend.append('excel_file', excelFile);

        response = await axios.post(apiEndpoint, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        });
      } else {
        // ✅ PAID MODE: JSON payload (UNCHANGED - working)
        response = await axios.post(apiEndpoint, payload, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
      }

      console.log("✅ SUCCESS RESPONSE:", response.data);

      const regId = response.data?.registration?.id || response.data?.id;

      if (!regId) {
        toast.error("Registration ID not returned from server.");
        return;
      }

      setRegistrationId(regId);
      setDialogMessage(
        isFreeMode 
          ? "We will send you an email once your list is approved by the admin." 
          : "Organization registered successfully! You can now proceed to payment."
      );
      toast.success("Organization registered successfully!");
      setIsDialogOpen(true);
    } catch (error) {
      console.error("❌ ERROR:", error.response?.data || error);
      if (error.response?.status === 422) {
        toast.error("Validation error: " + (error.response.data.message || "Check your inputs"));
      } else if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || "Registration failed.";
        toast.error(errorMessage);
      } else {
        toast.error("Network error. Please check your connection.");
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleInitiatePayment = async () => {
    if (!registrationId) {
      toast.error("Registration ID not found.");
      return;
    }

    try {
      const payload = { registration_id: registrationId };

      const res = await axios.post(
        "https://eventscons.com/eventbackendnew/api/group-registration/initiate-payment",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const approvalUrl = res.data?.approval_url;

      if (approvalUrl) {
        window.open(approvalUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Approval URL not found in response.");
        console.error("Payment response:", res.data);
      }
    } catch (error) {
      if (error.response) {
        console.error("Payment API Error:", error.response.data);
        const errorMessage = error.response.data.error || "Payment initiation failed.";
        toast.error(errorMessage);
      } else {
        console.error("Network or other error:", error);
        toast.error("Something went wrong while initiating payment.");
      }
    }
  };

  // ✅ FIXED: Smart phone validation (WhatsApp OR Phone) + Excel validation
  const handleRegister = (e) => {
    e.preventDefault();

    let errorOrganizationName = "";
    let errorContactPerson = "";
    let errorPhone = "";
    let errorPassword = "";
    let errorEmail = "";
    let errorDoctorsRegistered = "";
    let errorWhatsappNumber = "";
    let errorCountryOfResidence = "";
    let errorTitle = "";
    let errorExcelFile = "";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!organizationName) errorOrganizationName = "Please enter the organization name.";
    if (!contactPerson) errorContactPerson = "Please enter the contact person.";
    if (!title) errorTitle = "Please select a salutation.";
    if (!getFinalPhone()) errorPhone = "Please enter phone OR WhatsApp number.";  // ✅ FIXED
    if (!email) errorEmail = "Please enter the email.";
    else if (!emailRegex.test(email)) errorEmail = "Please enter a valid email.";
    if (!countryOfResidence) errorCountryOfResidence = "Please select your country.";

    if (isFreeMode && (!excelFile || excelFileName === "")) {
      errorExcelFile = "Excel file is mandatory for free registration.";
    }

    if (selectedGroupPrices.length > 0 && totalDoctors <= 0) {
      errorDoctorsRegistered = "Please enter number of doctors for at least one package.";
    }

    setError({
      organizationName: errorOrganizationName,
      contactPerson: errorContactPerson,
      phone: errorPhone,
      password: errorPassword,
      email: errorEmail,
      doctorsRegistered: errorDoctorsRegistered,
      whatsappNumber: errorWhatsappNumber,
      countryOfResidence: errorCountryOfResidence,
      title: errorTitle,
      excelFile: errorExcelFile,
    });

    if (
      organizationName &&
      contactPerson &&
      getFinalPhone() &&
      password &&
      emailRegex.test(email) &&
      countryOfResidence &&
      title &&
      !errorDoctorsRegistered &&
      (!isFreeMode || (excelFile && excelFileName))
    ) {
      handleSubmit();
    }
  };

  return (
    <Container
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "20vh",
      }}
    >
      <DialogMessage
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        message={dialogMessage}
      />

      <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Grid xs={12} sm={12} md={6} lg={6} xl={6}>
          <form onSubmit={handleRegister}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '20px' }}
              >
                <Typography sx={{ padding: "0px", color: "#c62828" }} variant="h4">
                  Register Organization
                </Typography>

                <Typography variant="body1" sx={{ marginTop: "10px" }}>
                  <Button
                    sx={{
                      color: "white",
                      backgroundColor: "#9B1321",
                      "&:hover": { backgroundColor: darken("#dc143c", 0.2) },
                    }}
                    onClick={() => navigate("/login")}
                  >
                    login
                  </Button>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {selectedGroupPrices.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Selected Group Packages
                    </Typography>

                    {selectedGroupPrices.map((pkg, idx) => (
                      <Typography key={pkg.id} variant="body2">
                        Package {idx + 1}: {pkg.package_name ? pkg.package_name : `#${pkg.id}`} —{" "}
                        {pkg.type === "free" ? "Free" : `$${pkg.price_per_person} per person`}
                      </Typography>
                    ))}
                  </>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Organization Name"
                  error={!!error.organizationName}
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  helperText={error.organizationName}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!error.title}>
                  <InputLabel id="salutation-label">Salutation</InputLabel>
                  <Select
                    labelId="salutation-label"
                    value={title}
                    label="Salutation"
                    onChange={(e) => setTitle(e.target.value)}
                  >
                    {salutations.map((salutation) => (
                      <MenuItem key={salutation.value} value={salutation.value}>
                        {salutation.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="error">
                    {error.title}
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Person"
                  error={!!error.contactPerson}
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  helperText={error.contactPerson}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email"
                  error={!!error.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  helperText={error.email}
                  fullWidth
                  type="email"
                />
              </Grid>

              <input type="hidden" value={password} readOnly />

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink style={{ marginBottom: "8px" }}>
                    Phone Number
                  </InputLabel>
                  <PhoneInput
                    country={"jo"}
                    value={phone}
                    onChange={setPhone}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderColor: error.phone ? "red" : "#c4c4c4",
                      borderRadius: "4px",
                    }}
                    placeholder="Enter your phone number"
                  />
                  <Typography variant="caption" color="error">
                    {error.phone}
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink style={{ marginBottom: "8px" }}>
                    WhatsApp Number
                  </InputLabel>
                  <PhoneInput
                    country={"jo"}
                    value={whatsappNumber}
                    onChange={setWhatsappNumber}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderColor: error.whatsappNumber ? "red" : "#c4c4c4",
                      borderRadius: "4px",
                    }}
                    placeholder="Enter your WhatsApp number"
                  />
                  <Typography variant="caption" color="error">
                    {error.whatsappNumber}
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!error.countryOfResidence}>
                  <InputLabel id="country-label">Nationality / Country</InputLabel>

                  <Select
                    labelId="country-label"
                    label="Nationality / Country"
                    value={countryOfResidence}
                    onChange={(e) => setCountryOfResidence(e.target.value)}
                    disabled={loadingCountries}
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.id} value={c.name}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={c.flag_url}
                            alt={`${c.name} flag`}
                            width={22}
                            height={16}
                            style={{ borderRadius: 2, objectFit: "cover" }}
                            loading="lazy"
                          />
                          <span>{c.name}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography variant="caption" color="error">
                    {error.countryOfResidence}
                  </Typography>
                </FormControl>
              </Grid>

              {/* ✅ EXCEL FIELD - FREE MODE ONLY */}
            

              {/* ✅ Doctors section ALWAYS visible - but price 0 in free mode */}
              {selectedGroupPrices.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                    Number of Doctors per Package
                  </Typography>

                  <Grid container spacing={2}>
                    {selectedGroupPrices.map((pkg, idx) => (
                      <Grid item xs={12} key={pkg.id}>
                        <TextField
                          fullWidth
                          type="number"
                          placeholder={`Doctors for Package ${idx + 1}`}
                          value={doctorsPerPackage[pkg.id] || ""}
                          onChange={(e) => handleDoctorsChange(pkg.id, e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                        <Typography variant="caption" sx={{ color: "#777" }}>
                          {isFreeMode 
                            ? "Package Type: Free (No charge)" 
                            : `Price: $${pkg.price_per_person} per person`
                          }
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {error.doctorsRegistered && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                      {error.doctorsRegistered}
                    </Typography>
                  )}
                </Grid>
              )}
                {isFreeMode && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#c62828" }}>
                    Excel File*
                  </Typography>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileChange}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: error.excelFile ? "2px dashed red" : "2px dashed #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      cursor: "pointer",
                    }}
                  />
                  {excelFileName && (
                    <Typography variant="body2" sx={{ mt: 1, color: "#1976d2", fontWeight: 500 }}>
                      ✅ Selected: {excelFileName}
                    </Typography>
                  )}
                  {error.excelFile && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                      {error.excelFile}
                    </Typography>
                  )}
                </Grid>
              )}

              {/* ✅ Free mode message */}
              

              {/* Cancellation policy checkbox */}
              <Grid item xs={12}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input
                    type="checkbox"
                    name="accept_cancellation_policy"
                    checked={formData.accept_cancellation_policy}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        accept_cancellation_policy: e.target.checked,
                      }))
                    }
                    required
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                    I agree to the{" "}
                    <a
                      href="/cancellation-and-refund-policy"
                      style={{ fontWeight: "700", textDecoration: "underline" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cancellation and Refund Policy
                    </a>
                    . I confirm that I have read and accepted the terms in case of cancellation or
                    no-show. Link to{" "}
                    <a
                      href="/terms-and-conditions"
                      style={{ fontWeight: "700", textDecoration: "underline" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms and conditions
                    </a>{" "}
                    (we will provide it to you) to open in another page so he check ✔️ and move on.
                  </span>
                </div>
              </Grid>

              {/* ✅ Total Amount + Buttons - HIDE completely in free mode */}
              {!isFreeMode && (
                <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <div
                    style={{
                      backgroundColor: "#f8f8f8",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555", mb: 0.5 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#9B1321" }}>
                      {grandTotal > 0 ? `$${grandTotal.toFixed(2)}` : "$0.00"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#777" }}>
                      Total doctors: {totalDoctors} across selected packages
                    </Typography>
                  </div>

                  <Button
                    sx={{
                      backgroundColor: "#9B1321",
                      "&:hover": { backgroundColor: darken("#dc143c", 0.2) },
                      color: "#ffffff",
                      width: "100%",
                      minHeight: "48px",
                    }}
                    type="submit"
                    disabled={registering}
                  >
                    {registering ? "Registering..." : "Register"}
                  </Button>

                  {shouldShowPayNow && (
                    <Button
                      sx={{
                        backgroundColor: "#0069d9",
                        "&:hover": { backgroundColor: darken("#0069d9", 0.2) },
                        color: "#ffffff",
                        width: "100%",
                        minHeight: "48px",
                      }}
                      type="button"
                      disabled={registrationId == null}
                      onClick={handleInitiatePayment}
                    >
                      Pay Now
                    </Button>
                  )}
                </Grid>
              )}

              {/* ✅ Register button ONLY for free mode (no total amount box) */}
              {isFreeMode && (
                <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <Button
                    sx={{
                      backgroundColor: "#9B1321",
                      "&:hover": { backgroundColor: darken("#dc143c", 0.2) },
                      color: "#ffffff",
                      width: "100%",
                      minHeight: "48px",
                    }}
                    type="submit"
                    disabled={registering}
                  >
                    {registering ? "Uploading Excel..." : "Register Free"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RegisterGroupPage;